import { computed } from 'vue'
import { useProfileStore } from '@/stores/profile'
import {
    PERMISSIONS_MATRIX,
    PERMISSIONS_BY_GROUP,
    ROLE_TO_GROUPS,
    GROUP_TO_ROLES,
    PERMISSION_GROUPS,
    ROLE_HIERARCHY,
    hasHigherPrivileges,
    getRolesWithEqualOrHigherPrivileges,
    getRolesWithLowerPrivileges,
    type PermissionAction,
    type UserRole,
    type UserGroup
} from '@/config/permissions'

// Types ré-exportés depuis config/permissions

/**
 * Composable pour gérer les permissions utilisateur
 */
export function usePermissions() {
    const profileStore = useProfileStore()

    // SuperAdmin (contrôle direct via user_type OU appartenance au groupe Admin)
    const isSuperAdmin = computed<boolean>(() => {
        // Vérifier user_type = 'superadmin'
        const raw = (profileStore.profile?.user_type as string | undefined) || ''
        const isSuperAdminType = raw.toString().trim().toLowerCase() === 'superadmin'

        // Vérifier l'appartenance au groupe 'Admin'
        const groups = (profileStore.profile?.groups || []) as Array<{ name?: string }>
        const normalizedGroupNames = groups
            .map(g => (g?.name ?? '').toString().trim().toLowerCase())
            .filter(Boolean)
        const isInAdminGroup = normalizedGroupNames.includes('admin')

        return isSuperAdminType || isInAdminGroup
    })

    // Rôle actuel de l'utilisateur
    const currentRole = computed<UserRole>(() => {
        // Priorité aux groupes (on parcourt tous les groupes)
        const groups = (profileStore.profile?.groups || []) as Array<{ name?: string }>
        const normalizedGroupNames = groups
            .map(g => (g?.name ?? '').toString().trim().toLowerCase())
            .filter(Boolean)

        // Si l'utilisateur possède plusieurs groupes, on applique une priorité stable
        const priority: Array<[key: string, role: UserRole]> = [
            ['admin', 'Admin'],
            ['responsable pedagogique', 'Responsable pedagogique'],
            ['secretaire', 'Secretaire'],
            ['enseignant', 'Enseignant'],
            ['etudiant', 'Etudiant'],
        ]
        for (const [key, role] of priority) {
            if (normalizedGroupNames.includes(key) && ROLE_HIERARCHY.includes(role)) {
                return role
            }
        }

        // Fallback sur user_type si aucun groupe ne matche nos rôles
        const raw = (profileStore.profile?.user_type as string | undefined) || ''
        const normalized = raw.toString().trim().toLowerCase()
        const roleMap: Record<string, UserRole> = {
            admin: 'Admin',
            'responsable pedagogique': 'Responsable pedagogique',
            secretaire: 'Secretaire',
            enseignant: 'Enseignant',
            etudiant: 'Etudiant',
            superadmin: 'Admin',
            personnel: 'Enseignant', // par défaut pour "personnel"
        }
        const mapped = roleMap[normalized]
        if (mapped && ROLE_HIERARCHY.includes(mapped)) return mapped

        return 'Etudiant'
    })

    // Groupes de l'utilisateur actuel
    const currentGroups = computed<UserGroup[]>(() => {
        // Utiliser directement tous les groupes du profil (dédupliqués)
        const groups = (profileStore.profile?.groups || []) as Array<{ name?: string }>
        const names = groups
            .map(g => (g?.name ?? '').toString().trim())
            .filter(name => name.length > 0)

        // Mapper les noms de groupes de l'API vers les groupes standardisés
        // Les noms viennent exactement comme dans l'API : "Admin", "Enseignant", "Etudiant", etc.
        const groupMapping: Record<string, UserGroup> = {
            // Groupes de base (noms exacts de l'API)
            'Admin': 'Administration',
            'Enseignant': 'Enseignement',
            'Etudiant': 'Etudiants',
            'Responsable pedagogique': 'Pedagogie',
            'RH': 'RH',
            'Maintenance': 'Maintenance',
            'Comptable': 'Comptabilite',
            'Personnel': 'Enseignement', // Personnel → Enseignement
            'Superviseur': 'Direction', // Superviseur → Direction
            // Variantes insensibles à la casse (pour robustesse)
            'admin': 'Administration',
            'enseignant': 'Enseignement',
            'etudiant': 'Etudiants',
            'responsable pedagogique': 'Pedagogie',
            'rh': 'RH',
            'maintenance': 'Maintenance',
            'comptable': 'Comptabilite',
            'personnel': 'Enseignement',
            'superviseur': 'Direction',
            // Noms standardisés (pour compatibilité)
            'Administration': 'Administration',
            'Enseignement': 'Enseignement',
            'Etudiants': 'Etudiants',
            'Pedagogie': 'Pedagogie',
            'Secretariat': 'Secretariat',
            'Direction': 'Direction',
            'Comptabilite': 'Comptabilite',
            'comptabilite': 'Comptabilite',
        }

        // Mapper les noms (d'abord avec le nom exact, puis en minuscules si pas trouvé)
        const mappedNames = names.map(name => {
            // Essayer d'abord avec le nom exact
            if (groupMapping[name]) {
                return groupMapping[name]
            }
            // Puis essayer en minuscules
            const normalized = name.toLowerCase()
            return groupMapping[normalized] || name
        })
        const unique = Array.from(new Set(mappedNames))

        // Ne retenir que les groupes connus de notre config
        const allowed: UserGroup[] = [
            'Administration',
            'Pedagogie',
            'Secretariat',
            'Enseignement',
            'Etudiants',
            'Direction',
            'Comptabilite',
            'RH',
            'Maintenance'
        ] as UserGroup[]
        return unique.filter(n => allowed.includes(n as UserGroup)) as UserGroup[]
    })

    // Vérifier si l'utilisateur a une permission spécifique (union de tous ses groupes)
    const hasPermission = (action: PermissionAction): boolean => {
        if (isSuperAdmin.value) return true

        const userGroups = currentGroups.value

        // Vérification par groupe (priorité absolue)
        const allowedGroups = PERMISSIONS_BY_GROUP[action] || []
        const hasGroupPermission = userGroups.some(group => allowedGroups.includes(group))

        if (hasGroupPermission) return true

        // Fallback par rôle (si aucun groupe ne donne la permission)
        const userRole = currentRole.value
        const allowedRoles = PERMISSIONS_MATRIX[action] || []
        const hasRolePermission = allowedRoles.includes(userRole)

        return hasRolePermission
    }

    // Vérifier si l'utilisateur a au moins une des permissions
    const hasAnyPermission = (actions: PermissionAction[]): boolean => {
        if (isSuperAdmin.value) return true
        return actions.some(action => hasPermission(action))
    }

    // Vérifier si l'utilisateur a toutes les permissions
    const hasAllPermissions = (actions: PermissionAction[]): boolean => {
        if (isSuperAdmin.value) return true
        return actions.every(action => hasPermission(action))
    }

    // Obtenir toutes les permissions de l'utilisateur actuel (CORRIGÉ)
    const getUserPermissions = computed((): PermissionAction[] => {
        return (Object.keys(PERMISSIONS_MATRIX) as PermissionAction[])
            .filter(action => hasPermission(action))
    })

    // Vérifier si l'utilisateur est dans un rôle spécifique
    const hasRole = (role: UserRole): boolean => {
        // SuperAdmin n'est PAS considéré comme ayant tous les rôles
        // Il a toutes les PERMISSIONS, mais pas tous les RÔLES
        return currentRole.value === role
    }

    // Vérifier si l'utilisateur est dans l'un des rôles
    const hasAnyRole = (roles: UserRole[]): boolean => {
        // SuperAdmin a accès à tout mais n'a pas "tous les rôles"
        // On vérifie juste son currentRole
        return roles.includes(currentRole.value)
    }

    // Vérifier si l'utilisateur est dans un groupe spécifique
    const hasGroup = (group: UserGroup): boolean => {
        if (isSuperAdmin.value) return true
        return currentGroups.value.includes(group)
    }

    // Vérifier si l'utilisateur est dans l'un des groupes
    const hasAnyGroup = (groups: UserGroup[]): boolean => {
        if (isSuperAdmin.value) return true
        return groups.some(group => currentGroups.value.includes(group))
    }

    // Vérifier si l'utilisateur est dans tous les groupes
    const hasAllGroups = (groups: UserGroup[]): boolean => {
        if (isSuperAdmin.value) return true
        return groups.every(group => currentGroups.value.includes(group))
    }

    // Obtenir les permissions pour un rôle spécifique
    const getRolePermissions = (role: UserRole): PermissionAction[] => {
        return (Object.keys(PERMISSIONS_MATRIX) as PermissionAction[])
            .filter(action => PERMISSIONS_MATRIX[action].includes(role))
    }

    // Obtenir les permissions pour un groupe spécifique
    const getGroupPermissions = (group: UserGroup): PermissionAction[] => {
        return (Object.keys(PERMISSIONS_BY_GROUP) as PermissionAction[])
            .filter(action => PERMISSIONS_BY_GROUP[action].includes(group))
    }

    // Obtenir les groupes d'un rôle
    const getRoleGroups = (role: UserRole): UserGroup[] => {
        return ROLE_TO_GROUPS[role] || []
    }

    // Obtenir les rôles d'un groupe
    const getGroupRoles = (group: UserGroup): UserRole[] => {
        return GROUP_TO_ROLES[group] || []
    }

    // Vérifier les permissions avec contexte (pour des cas spéciaux)
    const hasPermissionWithContext = (
        action: PermissionAction,
        context?: Record<string, any>
    ): boolean => {
        const basePermission = hasPermission(action)

        // Logique conditionnelle supplémentaire
        switch (action) {
            case 'delete_student':
                // Permettre la suppression de son propre profil
                if (context?.isOwnProfile) {
                    return true
                }
                break

            case 'update_student':
                // Permettre la modification de son propre profil
                if (context?.isOwnProfile) {
                    return true
                }
                break

            case 'manage_grades':
                // Les enseignants ne peuvent gérer que leurs propres cours
                if (hasRole('Enseignant') && context?.isOwnCourse) {
                    return true
                }
                break
        }

        return basePermission
    }

    // Utilitaires pour les composants Vue - Rôles
    const isStudent = computed(() => hasRole('Etudiant'))
    const isTeacher = computed(() => hasRole('Enseignant'))
    const isAdmin = computed(() => hasRole('Admin'))
    const isSecretary = computed(() => hasRole('Secretaire'))
    const isPedagogicalManager = computed(() => hasRole('Responsable pedagogique'))

    // Utilitaires pour les composants Vue - Groupes
    const isInAdministration = computed(() => hasGroup('Administration'))
    const isInPedagogie = computed(() => hasGroup('Pedagogie'))
    const isInSecretariat = computed(() => hasGroup('Secretariat'))
    const isInEnseignement = computed(() => hasGroup('Enseignement'))
    const isInEtudiants = computed(() => hasGroup('Etudiants'))
    const isInDirection = computed(() => hasGroup('Direction'))
    const isComptable = computed(() => hasGroup('Comptabilite'))

    // Fonctions utilitaires pour les utilisateurs multi-groupes
    const getHighestRole = (): UserRole => {
        if (isSuperAdmin.value) return 'Admin'

        const userGroups = currentGroups.value
        const priority: Array<[key: string, role: UserRole]> = [
            ['admin', 'Admin'],
            ['responsable pedagogique', 'Responsable pedagogique'],
            ['secretaire', 'Secretaire'],
            ['enseignant', 'Enseignant'],
            ['etudiant', 'Etudiant'],
        ]

        for (const [key, role] of priority) {
            if (userGroups.some(group => group.toLowerCase().includes(key))) {
                return role
            }
        }

        return currentRole.value
    }

    const getAllPossibleRoles = (): UserRole[] => {
        if (isSuperAdmin.value) return ['Admin']

        const userGroups = currentGroups.value
        const roles: UserRole[] = []

        // Mapper chaque groupe vers ses rôles possibles
        const groupToRoles: Record<string, UserRole[]> = {
            'admin': ['Admin'],
            'responsable pedagogique': ['Responsable pedagogique'],
            'secretaire': ['Secretaire'],
            'enseignant': ['Enseignant'],
            'etudiant': ['Etudiant'],
            'direction': ['Admin', 'Responsable pedagogique']
        }

        userGroups.forEach(group => {
            const groupKey = group.toLowerCase()
            if (groupToRoles[groupKey]) {
                roles.push(...groupToRoles[groupKey])
            }
        })

        return Array.from(new Set(roles)) // Dédupliquer
    }

    const hasPermissionInContext = (action: PermissionAction, context: 'teaching' | 'management' | 'administration'): boolean => {
        if (isSuperAdmin.value) return true

        const userGroups = currentGroups.value

        // Contexte d'enseignement : priorité aux groupes d'enseignement
        if (context === 'teaching') {
            const teachingGroups: UserGroup[] = ['Enseignement', 'Pedagogie']
            if (userGroups.some(group => teachingGroups.includes(group))) {
                return hasPermission(action)
            }
        }

        // Contexte de gestion : priorité aux groupes de gestion
        if (context === 'management') {
            const managementGroups: UserGroup[] = ['Pedagogie', 'Administration', 'Secretariat']
            if (userGroups.some(group => managementGroups.includes(group))) {
                return hasPermission(action)
            }
        }

        // Contexte d'administration : priorité aux groupes d'administration
        if (context === 'administration') {
            const adminGroups: UserGroup[] = ['Administration', 'Secretariat']
            if (userGroups.some(group => adminGroups.includes(group))) {
                return hasPermission(action)
            }
        }

        return hasPermission(action)
    }

    return {
        // État - Rôles
        currentRole,
        isStudent,
        isTeacher,
        isAdmin,
        isSecretary,
        isPedagogicalManager,

        // État - Groupes
        currentGroups,
        isInAdministration,
        isInPedagogie,
        isInSecretariat,
        isInEnseignement,
        isInEtudiants,
        isInDirection,
        isComptable,

        // Méthodes de vérification - Permissions
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        hasPermissionWithContext,

        // Méthodes de vérification - Rôles
        hasRole,
        hasAnyRole,

        // Méthodes de vérification - Groupes
        hasGroup,
        hasAnyGroup,
        hasAllGroups,

        // Utilitaires - Permissions
        getUserPermissions,
        getRolePermissions,
        getGroupPermissions,

        // Utilitaires - Rôles et Groupes
        getRoleGroups,
        getGroupRoles,

        // Configuration (en lecture seule)
        PERMISSIONS_MATRIX: Object.freeze(PERMISSIONS_MATRIX),
        PERMISSIONS_BY_GROUP: Object.freeze(PERMISSIONS_BY_GROUP),
        ROLE_TO_GROUPS: Object.freeze(ROLE_TO_GROUPS),
        GROUP_TO_ROLES: Object.freeze(GROUP_TO_ROLES),
        PERMISSION_GROUPS: Object.freeze(PERMISSION_GROUPS),
        ROLE_HIERARCHY: Object.freeze(ROLE_HIERARCHY),

        // Utilitaires de hiérarchie
        hasHigherPrivileges,
        getRolesWithEqualOrHigherPrivileges,
        getRolesWithLowerPrivileges,

        // Fonctions pour utilisateurs multi-groupes
        getHighestRole,
        getAllPossibleRoles,
        hasPermissionInContext,

        // SuperAdmin
        isSuperAdmin,
    }
}

// Types disponibles via l'import depuis config/permissions
